import {
  AppShell,
  Button,
  Group,
  Modal,
  Skeleton,
  Table,
  Loader,
} from "@mantine/core";

import { JSX, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";
import { useLocation } from "wouter";
import {
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import {
  useGetVentQuery,
  DeleteCustomFieldRequest,
  useDeleteCustomFieldMutation,
  MoveCustomFieldRequest,
  useMoveCustomFieldMutation,
} from "../slices/api/ventsApi";
import { openConfirmationModal } from "../shared/components/Notifications";
import { formatCustomFieldType } from "../slices/types";
import {
  IconAddNew,
  IconDelete,
  IconEdit,
  IconMoveDown,
  IconMoveUp,
} from "../shared/components/Icons";
import { EbTooltip } from "../shared/components/EbTooltip";
import CreateCustomField from "./CreateCustomField";
import { EbActionIcon } from "../shared/components/EbActionItem";
import { setConsoleIds } from "./Console";
import { TabHeader } from "../shared/components/TabHeader";

interface Props {
  ventId: string;
  setEventTabDescription: (name: string) => void;
}

function CustomFieldsTab(props: Props) {
  const ventId = props.ventId;
  const [_, navigate] = useLocation();

  const [opened, { open, close }] = useDisclosure(false);
  const [deleteCustomField] = useDeleteCustomFieldMutation();
  const [moveCustomField] = useMoveCustomFieldMutation();
  const {
    data: ventResponse,
    isFetching: isFetchingVent,
    isSuccess: isSuccessVent,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: ventId,
  });

  useEffect(() => {
    if (ventResponse) {
      props.setEventTabDescription(ventResponse.name);
    }
  }, [ventResponse]);

  const handleDelete = async (customFieldId: string) => {
    if (ventResponse == null) {
      console.error("vent is null but should not be");
      return;
    }

    const request: DeleteCustomFieldRequest = {
      customFieldId,
      ventId,
      updateTime: ventResponse.updateTime!,
    };

    try {
      await deleteCustomField(request).unwrap();
      setConsoleIds({ customFieldId: null });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  const handleMove = async (customFieldId: string, newIndex: number) => {
    const request: MoveCustomFieldRequest = {
      id: customFieldId,
      newIndex,
      ventId: ventId,
      updateTime: ventResponse?.updateTime!,
    };

    try {
      await moveCustomField(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }

  if (!ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }
  let rows: JSX.Element[] = [];
  if (
    isSuccessVent &&
    ventResponse != null &&
    (ventResponse.customFields ?? []).length > 0
  ) {
    rows = ventResponse.customFields.map((field, index) => {
      return (
        <Table.Tr key={field.id}>
          <Table.Td>
            <Group gap="0.5rem">
              <EbActionIcon
                disabled={!ventResponse.canUserEdit}
                aria-label="Edit field"
                onClick={() => {
                  navigate(
                    `/console/customfield/event/${ventId}/customfield/${field.id}`
                  );
                }}
              >
                <EbTooltip label="Edit field">
                  <IconEdit />
                </EbTooltip>
              </EbActionIcon>
              <EbActionIcon
                disabled={!ventResponse.canUserEdit}
                aria-label="Delete field"
                onClick={() => {
                  openConfirmationModal({
                    message: `Please confirm deletion of the field ${field.name}`,
                    title: "Delete Activity",
                    onConfirm: () => handleDelete(field.id),
                  });
                }}
              >
                <EbTooltip label="Delete field">
                  <IconDelete />
                </EbTooltip>
              </EbActionIcon>
              <EbActionIcon
                disabled={!ventResponse.canUserEdit || index == 0}
                aria-label="Move up"
                onClick={() => {
                  handleMove(field.id, index - 1);
                }}
              >
                <EbTooltip label="Move up">
                  <IconMoveUp />
                </EbTooltip>
              </EbActionIcon>
              <EbActionIcon
                disabled={
                  !ventResponse.canUserEdit ||
                  index === ventResponse.customFields.length - 1
                }
                aria-label="Move down"
                onClick={() => {
                  handleMove(field.id, index + 1);
                }}
              >
                <EbTooltip label="Move down">
                  <IconMoveDown />
                </EbTooltip>
              </EbActionIcon>
            </Group>
          </Table.Td>
          <Table.Td>{field.name}</Table.Td>
          <Table.Td>{formatCustomFieldType(field.fieldType)}</Table.Td>
          <Table.Td>{field.description}</Table.Td>
          <Table.Td>{field.hidden ? "Hidden" : ""}</Table.Td>
        </Table.Tr>
      );
    });
  }
  return (
    <AppShell.Main>
      <Modal opened={opened} onClose={close} withCloseButton={false} centered>
        <CreateCustomField
          title={`Add Custom Field for ${ventResponse.name}`}
          onClose={close}
          ventId={ventId}
          updateTime={ventResponse.updateTime!}
        />
      </Modal>
      <TabHeader
        label="Custom Fields for"
        ventId={ventId}
        ventName={ventResponse.name}
      />
      <Group>
        <Button
          disabled={!ventResponse.canUserEdit}
          onClick={open}
          rightSection={<IconAddNew />}
        >
          Add Custom Field
        </Button>
      </Group>
      <Skeleton visible={isFetchingVent}>
        <div hidden={!isSuccessVent || rows.length === 0}>
          <Table.ScrollContainer minWidth={500}>
            <Table stickyHeader stickyHeaderOffset={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Actions</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Hidden</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </div>
        <div hidden={isFetchingVent || rows.length > 0}>
          There are no custom fields.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

export default CustomFieldsTab;
